import { lazy } from "react";
import { Navigate, Route, Routes, useLocation, useOutletContext } from "react-router-dom";
import Casca from "./Casca.jsx";
import Conversa from "./componentes/Conversa.jsx";
import Mesa from "./vistas/Mesa.jsx";

/*
 * A mesa abre com a mesa, e o resto chega quando alguém for.
 *
 * As onze vistas viviam no mesmo pacote da abertura: mais de um megabyte de
 * JavaScript para o navegador ler, compilar e executar ANTES de a primeira
 * conversa aparecer — medido em 1,1s de tarefa bloqueada num aparelho quatro
 * vezes mais lento que esta máquina, e é nesse quadro que a abertura da mesa
 * engasga. O Kanban de Documentos, a régua de Fluxos e o quadro de Tarefas (com
 * a agenda dentro) não têm por que pesar na porta de entrada de quem só vai
 * responder um cliente.
 *
 * Atendimentos fica de fora da divisão de propósito: é onde a mesa POUSA (a raiz
 * redireciona para lá), e dividir o pacote da tela que abre em seguida só
 * adicionaria uma ida à rede no caminho crítico. Quem manda a peça pesada de
 * verdade — o editor de .docx — já vinha dividido em `TelaPeticao`.
 */
const Tarefas = lazy(() => import("./vistas/Tarefas.jsx"));
const Rapidas = lazy(() => import("./vistas/Rapidas.jsx"));
const Contratos = lazy(() => import("./vistas/Contratos.jsx"));
/* Publicações é dividida como as outras: quem só veio responder um cliente não
   paga o pacote da fila do diário. */
const Publicacoes = lazy(() => import("./vistas/Publicacoes.jsx"));
const AgentesIA = lazy(() => import("./vistas/AgentesIA.jsx"));
const Reunioes = lazy(() => import("./vistas/Reunioes.jsx"));
const Documentos = lazy(() => import("./vistas/Documentos.jsx"));
const Equipe = lazy(() => import("./vistas/Equipe.jsx"));
const Ajustes = lazy(() => import("./vistas/Ajustes.jsx"));
/* A conversa com os agentes ACP: transcrição, bolhas, markdown e a caixa de
   escrever. Peso que não pode entrar no pacote de quem só veio responder um
   cliente — e a tela mais rara da mesa. */
const Agentes = lazy(() => import("./vistas/Agentes.jsx"));
const Notificacoes = lazy(() => import("./vistas/Notificacoes.jsx"));
/* A carteira de contatos carrega o recharts (os gráficos do painel): peso que
   não pode entrar no pacote de quem só veio responder um cliente. */
const Contatos = lazy(() => import("./vistas/Contatos.jsx"));
/* A ajuda carrega o manual inteiro (texto, prints e vídeos): peso que não pode
   entrar no pacote de quem só veio responder um cliente. */
const Ajuda = lazy(() => import("./vistas/Ajuda.jsx"));
/* "O Astrea caiu?" é tela de exceção — abre quando alguém desconfia da
   integração, não todo dia. Nem um byte dela no pacote da abertura. */
const Astrea = lazy(() => import("./vistas/Astrea.jsx"));
/* Setores e Sistema são do master e do dev — telas raras, fora do pacote de abertura. */
const Setores = lazy(() => import("./vistas/Setores.jsx"));
const Sistema = lazy(() => import("./vistas/Sistema.jsx"));
/* O painel de erros e telemetria: só master e dev, e raro — fora da abertura. */
const Observabilidade = lazy(() => import("./vistas/Observabilidade.jsx"));
/* Os primeiros passos são tela de uma semana na vida de cada pessoa: fora do
   pacote de todo dia. */
const PrimeirosPassos = lazy(() => import("./vistas/PrimeirosPassos.jsx"));
/* Criativos carrega o recharts: peso que não entra no pacote da abertura. */
const Criativos = lazy(() => import("./vistas/Criativos.jsx"));

/**
 * `/agenda` → `/tarefas`, com a visão de tempo escrita.
 *
 * `?visao=` já vem do endereço antigo nas visões de dia e de mês; sem ele o
 * padrão de lá era a semana, e o desta vista é o quadro — sem escrever a
 * semana, `/agenda` cairia no quadro de tarefas, que não é a tela que a pessoa
 * pediu.
 */
function comVisaoDeAgenda(search) {
  const busca = new URLSearchParams(search);
  if (!busca.get("visao")) busca.set("visao", "semana");
  return `?${busca}`;
}

/** O mesmo endereço, apontando para uma seção da tela nova, sem perder a busca. */
function naSecao(search, secao) {
  const busca = new URLSearchParams(search);
  busca.set("secao", secao);
  return `?${busca}`;
}

/** A conversa precisa saber se o painel empurra e se está na tela — vem da casca. */
function ConversaNaRota() {
  const { empurraPainel, painelNaTela, listaNaTela, aoAlternarLista, aoAlternarPainel } = useOutletContext();
  return (
    <Conversa
      empurraPainel={empurraPainel}
      painelNaTela={painelNaTela}
      listaNaTela={listaNaTela}
      aoAlternarLista={aoAlternarLista}
      aoAlternarPainel={aoAlternarPainel}
    />
  );
}

/**
 * O mapa da mesa.
 *
 * `/atendimentos/:id` é o endereço de um atendimento — dá para mandar por
 * mensagem, fixar na aba e voltar nele depois de recarregar. `/reunioes/:id`
 * abre a transcrição daquela gravação. O resto do recorte (busca, filtro, aba
 * do painel, petição aberta) viaja na query, porque é a mesma tela vista de
 * outro jeito, não outro lugar.
 */
export default function Rotas() {
  /* A query sobrevive à redireção da raiz: `/?avisos=1` (o link do sino, que
     circula por mensagem) caía em `/atendimentos` pelado, e o balão nunca abria
     — `<Navigate to="/atendimentos">` descarta a busca. É recorte da mesma tela,
     e recorte mora na URL. */
  const { search } = useLocation();
  return (
    <Routes>
      <Route element={<Casca />}>
        <Route index element={<Navigate to={`/atendimentos${search}`} replace />} />
        {/* Sem conversa aberta, `/atendimentos` é a MESA vista de fora: resumo,
            painel (a matriz etapa × responsável com a largura toda), kanban e
            tabela, em `?visao=`. Era só a saudação — uma tela de espera num lugar
            onde a pessoa passa dezenas de vezes por dia. */}
        <Route path="atendimentos" element={<Mesa />} />
        <Route path="atendimentos/:id" element={<ConversaNaRota />} />
        {/* Tarefas e agenda são o dia do escritório, não recorte da mesa: têm
            endereço próprio para mandar por mensagem ("teus prazos desta
            semana") e para abrir direto no celular.

            Eram DUAS telas — o quadro e a agenda —, e a segunda virou três
            visões da primeira (`?visao=dia|semana|mes`): o prazo é o mesmo
            `vence_em` dos dois lados. `/agenda` continua de pé levando a busca
            junto e caindo na semana, que era o padrão de lá: endereço que morre
            é erro no celular de alguém amanhã. */}
        <Route path="tarefas" element={<Tarefas />} />
        <Route path="agenda" element={<Navigate to={`/tarefas${comVisaoDeAgenda(search)}`} replace />} />
        <Route path="rapidas" element={<Rapidas />} />
        <Route path="contratos" element={<Contratos />} />
        {/* Publicações é LUGAR: "olha o que saiu no diário hoje" se manda por
            mensagem, e o recorte (situação, tribunal, período, a publicação
            aberta) viaja na query. */}
        <Route path="publicacoes" element={<Publicacoes />} />
        {/* Criativos é LUGAR: "olha o AD04 desta semana" se manda por mensagem, e
            o período viaja na query. */}
        <Route path="criativos" element={<Criativos />} />
        {/* A tela do WhatsApp do escritório morreu: a fila do aparelho era uma
            segunda caixa de entrada ao lado da mesa, e quem atende trabalha na
            mesa. Os dois endereços caem lá — em atendimentos —, porque endereço
            que morre é erro no celular de alguém amanhã. */}
        <Route path="whatsapp" element={<Navigate to="/atendimentos" replace />} />
        <Route path="caixa" element={<Navigate to="/atendimentos" replace />} />
        {/* `/contatos` voltou a ser LUGAR, e agora é outra coisa do que era: não
            é o catálogo do aparelho (aquele morreu com a caixa), é a carteira do
            escritório — a base inteira com o painel do recorte. O recorte viaja
            na query, então "os parados da Camila em insalubridade" é um link. */}
        <Route path="contatos" element={<Contatos />} />
        {/* Os fluxos viraram uma SEÇÃO da Central da IA: quando a IA age e quem
            fala são a mesma decisão, e estavam em duas portas. O endereço antigo
            leva à seção certa e carrega a busca junto (`?fluxo=9`, o editor
            aberto, circula por mensagem no escritório). */}
        <Route path="fluxos" element={<Navigate to={`/agentes-ia${naSecao(search, "quando")}`} replace />} />
        <Route path="agentes-ia" element={<AgentesIA />} />
        {/* O estúdio do agente é PÁGINA, não modal: criação e edição completas,
            com endereço que circula por mensagem ("novo" ou o id). */}
        <Route path="agentes-ia/:agenteIaId" element={<AgentesIA />} />
        <Route path="reunioes" element={<Reunioes />} />
        <Route path="reunioes/:gravacaoId" element={<Reunioes />} />
        <Route path="documentos" element={<Documentos />} />
        {/* `/conexao-astrea`, e não `/astrea`: o segundo já é servido pelo Hono
            (a bancada que o agente dirige) e é guardado por área na portaria —
            a tela do React não chegaria a carregar ali. */}
        <Route path="conexao-astrea" element={<Astrea />} />
        <Route path="equipe" element={<Equipe />} />
        <Route path="ajustes" element={<Ajustes />} />
        <Route path="setores" element={<Setores />} />
        <Route path="sistema" element={<Sistema />} />
        <Route path="observabilidade" element={<Observabilidade />} />
        <Route path="comecar" element={<PrimeirosPassos />} />
        {/* Assistentes tem endereço próprio, e a conversa aberta viaja na query
            (`/agentes?conversa=…`): link de conversa com o agente circula por
            mensagem no escritório como qualquer outro lugar da mesa. */}
        <Route path="agentes" element={<Agentes />} />
        <Route path="notificacoes" element={<Notificacoes />} />
        {/* Tópico da ajuda é LUGAR: `/ajuda/peticao` se manda por mensagem para
            o colega que perguntou. Sem tópico, abre o primeiro. */}
        <Route path="ajuda" element={<Ajuda />} />
        <Route path="ajuda/:topico" element={<Ajuda />} />
        {/* Endereço que não existe volta para a mesa em vez de dar tela branca. */}
        <Route path="*" element={<Navigate to="/atendimentos" replace />} />
      </Route>
    </Routes>
  );
}
